
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";

import axios from "../../../helpers/axios";
import { deleteMessageSocket, sendFileMessage } from "../../../actions/message";
import SiteMediaViewer from "../../../components/media/SiteMediaViewer";
import SecureMessageAttachment from "../../../components/media/SecureMessageAttachment";
import {
    downloadBlob,
    fetchChatAttachment,
    fetchChatAttachmentForView,
    fetchChatAttachmentPreview,
    fetchChatDrawingPreview,
    getChatAttachmentName,
    getChatAttachmentMimeType,
    getChatAttachmentType,
    hasSecureChatAttachment,
    shareBlobFile,
} from "../../../utils/chatAttachmentStore";
import { DOC_GROUPS, getFileExtensionLabel, getFileTypeLabel, isDrawingFile } from "../../../utils/mediaDocument";
import "../../../scss/components/_chat-file-card.scss";

// Вкладки окна. Порядок здесь = порядок на экране.
const TABS = [
    { key: "all", label: "Все" },
    { key: "image", label: "Фото" },
    { key: "video", label: "Видео" },
    { key: "doc", label: "Документы" },
    { key: "voice", label: "Голосовые" },
    { key: "link", label: "Ссылки" },
];

const labels = {
    all: "Все файлы",
    image: "Фотографии",
    video: "Видео",
    doc: "Документы",
    voice: "Голосовые",
    link: "Ссылки",
};

// Типы файлов в сводке по чату.
const SUMMARY_TYPES = [
    { key: "image", label: "Фото" },
    { key: "video", label: "Видео" },
    { key: "doc", label: "Документы" },
    { key: "voice", label: "Голосовые" },
];

const KIND_LABELS = {
    image: "Фото",
    video: "Видео",
    voice: "Голосовое",
    doc: "Документ",
};

const KIND_BADGES = {
    image: "ФОТО",
    video: "ВИДЕО",
    voice: "ЗВУК",
    doc: "ФАЙЛ",
};

const PAGE_LIMIT = 60;
const SEARCH_DELAY_MS = 300;
const MAX_UPLOAD_BYTES = 1024 * 1024 * 1024; // 1 ГБ на один файл
const MAX_TIMER_MS = 2147000000;

const ARCHIVE_ERRORS = {
    ARCHIVE_TOO_MANY_FILES: "Слишком много файлов для одного архива. Выберите меньше файлов.",
    ARCHIVE_EMPTY: "Нет файлов для архива.",
};

const IDLE_STATUS = { busy: false, text: "", error: "", link: "" };
const IDLE_UPLOAD = { busy: false, index: 0, total: 0, name: "", lines: [] };

const getId = (value) => String(value?._id || value?.id || "");

/** Чертёж DXF/SVG: в окне его можно открыть картинкой с зумом. */
const isDrawingItem = (item) => isDrawingFile(getChatAttachmentName(item, ""));

/** Что за файл — для подписи в списке: «Модель ArtCAM», «Чертёж DXF», «Фото»… */
const getItemTypeLabel = (item, kind) => (
    kind === "doc"
        ? getFileTypeLabel({ name: getChatAttachmentName(item, ""), mimeType: getChatAttachmentMimeType(item) }) || KIND_LABELS.doc
        : KIND_LABELS[kind]
);

const normalizeTab = (value) => (
    TABS.some((item) => item.key === value) ? value : "image"
);

const getSenderName = (item) => {
    const sender = item?.sender;

    if (!sender) return item?.guestSender?.name || item?.senderName || "Неизвестно";

    const name = [sender.firstName, sender.lastName].filter(Boolean).join(" ").trim();

    return name || sender.email || sender.username || item?.senderName || "Неизвестно";
};

const formatDate = (value) => {
    if (!value) return "";

    return new Date(value).toLocaleDateString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
    });
};

const formatBytes = (value) => {
    const bytes = Math.max(0, Number(value) || 0);

    if (bytes < 1024) return `${Math.round(bytes)} Б`;

    const units = ["КБ", "МБ", "ГБ"];
    let size = bytes / 1024;
    let index = 0;

    while (size >= 1024 && index < units.length - 1) {
        size /= 1024;
        index += 1;
    }

    const digits = index === 0 || size >= 100 ? 0 : 1;

    return `${size.toFixed(digits).replace(".", ",").replace(/,0$/, "")} ${units[index]}`;
};

const formatDuration = (value) => {
    const total = Math.round(Number(value) || 0);

    if (total <= 0) return "";

    const minutes = Math.floor(total / 60);
    const seconds = String(total % 60).padStart(2, "0");

    return `${minutes}:${seconds}`;
};

const pluralFiles = (count) => {
    const value = Math.abs(Number(count) || 0) % 100;
    const last = value % 10;

    if (value > 10 && value < 20) return "файлов";
    if (last === 1) return "файл";
    if (last >= 2 && last <= 4) return "файла";

    return "файлов";
};

const getServerText = (error) => {
    const data = error?.response?.data;
    const text = data?.error || data?.message;

    return typeof text === "string" && text.trim() ? text.trim() : "";
};

const getErrorText = (error, fallback) => {
    const serverText = getServerText(error);

    if (serverText) return serverText;
    if (error && !error.response) return "Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.";

    return fallback;
};

const getArchiveErrorText = (error) => {
    const code = error?.response?.data?.code;

    return getServerText(error) ||
        ARCHIVE_ERRORS[code] ||
        getErrorText(error, "Не удалось подготовить архив.");
};

// Ошибки отправки приходят из redux готовым текстом. Частые английские переводим.
const translateSendError = (text) => {
    const value = String(text || "").trim();

    if (!value) return "сервер не принял файл";
    if (/network error/i.test(value)) return "нет связи с сервером";
    if (/status code 413/i.test(value)) return "файл слишком большой для сервера";
    if (/timeout/i.test(value)) return "сервер не ответил вовремя";

    return value;
};

// Короткий список имён для сообщений: первые несколько и «ещё N».
const joinNames = (names = [], max = 4) => {
    const visible = names.slice(0, max).join(", ");
    const extra = names.length > max ? ` и ещё ${names.length - max}` : "";

    return `${visible}${extra}`;
};

// Одинаковые причины ошибок собираем в одну строку со счётчиком.
const groupReasons = (reasons = []) => {
    const counts = new Map();

    reasons.forEach((reason) => counts.set(reason, (counts.get(reason) || 0) + 1));

    return Array.from(counts.entries())
        .map(([reason, count]) => (count > 1 ? `${reason} (${count})` : reason))
        .join("; ");
};

const mergeItems = (prev, next) => {
    const known = new Set(prev.map(getId));

    return [...prev, ...next.filter((item) => !known.has(getId(item)))];
};

// Адрес архива = адрес API + путь, который вернул сервер.
const buildApiUrl = (path) => {
    const value = String(path || "");

    if (/^https?:\/\//i.test(value)) return value;

    const base = String(axios.defaults?.baseURL || "").replace(/\/+$/, "");

    return `${base}${value.startsWith("/") ? "" : "/"}${value}`;
};

// Архив может весить много гигабайт, поэтому его качает сам браузер,
// а не страница: обычный переход по ссылке.
const startBrowserDownload = (url, fileName) => {
    const link = document.createElement("a");

    link.href = url;
    if (fileName) link.download = fileName;
    link.rel = "noopener";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();
};

const hasDraggedFiles = (event) => (
    Array.from(event?.dataTransfer?.types || []).includes("Files")
);

// Из перетащенного берём только файлы. Папки браузер загрузить не может.
const pickDroppedFiles = (dataTransfer) => {
    const entries = Array.from(dataTransfer?.items || []);
    const canDetectFolders = entries.length > 0 && typeof entries[0].webkitGetAsEntry === "function";

    if (!canDetectFolders) {
        return { files: Array.from(dataTransfer?.files || []), folders: 0 };
    }

    const files = [];
    let folders = 0;

    entries.forEach((entry) => {
        if (entry.kind !== "file") return;

        if (entry.webkitGetAsEntry()?.isDirectory) {
            folders += 1;
            return;
        }

        const file = entry.getAsFile();
        if (file) files.push(file);
    });

    return { files, folders };
};

const getFileBadge = (name, kind) => (
    getFileExtensionLabel(name) || KIND_BADGES[kind] || KIND_BADGES.doc
);

function PickBox({ checked, onChange, name }) {
    return (
        <label className="media-check-AO" title={checked ? "Снять выбор" : "Выбрать"}>
            <input
                type="checkbox"
                checked={checked}
                onChange={onChange}
                aria-label={`Выбрать: ${name}`}
            />
        </label>
    );
}

// Значок файла. Для небольших фото показываем миниатюру, когда строка видна на экране.
function FileThumb({ item, kind, name }) {
    const nodeRef = useRef(null);
    const itemRef = useRef(item);
    const [visible, setVisible] = useState(false);
    const [url, setUrl] = useState("");

    itemRef.current = item;

    const messageId = getId(item);
    // Фото — лёгкая копия с сервера, чертёж DXF/SVG — его картинка.
    const drawing = kind === "doc" && isDrawingFile(name);
    const canPreview = (kind === "image" || drawing) && !!messageId;

    useEffect(() => {
        if (!canPreview || visible) return undefined;

        const node = nodeRef.current;

        if (!node || typeof IntersectionObserver === "undefined") {
            setVisible(true);
            return undefined;
        }

        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                setVisible(true);
                observer.disconnect();
            }
        }, { rootMargin: "200px" });

        observer.observe(node);

        return () => observer.disconnect();
    }, [canPreview, visible]);

    useEffect(() => {
        setUrl("");

        if (!canPreview || !visible) return undefined;

        let cancelled = false;
        let objectUrl = "";

        const load = drawing
            ? fetchChatDrawingPreview({ message: itemRef.current, size: "small" })
            : fetchChatAttachmentPreview({ message: itemRef.current, size: "small" });

        load
            .then((asset) => {
                if (cancelled || !asset?.blob) return;

                objectUrl = URL.createObjectURL(asset.blob);
                setUrl(objectUrl);
            })
            .catch(() => {
                // Миниатюра не обязательна: остаётся обычный значок.
            });

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [canPreview, drawing, visible, messageId]);

    return (
        <span ref={nodeRef} className={`media-file-AO__icon is-${kind}`} style={url && drawing ? { background: "#fff" } : undefined}>
            {url
                ? <img src={url} alt="" loading="lazy" decoding="async" />
                : <b>{getFileBadge(name, kind)}</b>}
        </span>
    );
}

// Строка файла во вкладке «Все».
const FileRow = React.memo(function FileRow({
    item,
    selected,
    expanded,
    downloading,
    onToggle,
    onOpen,
    onDownload,
    onShare,
}) {
    const id = getId(item);
    const kind = getChatAttachmentType(item);
    const name = getChatAttachmentName(item, KIND_LABELS[kind] || "Вложение");
    const size = Number(item?.fileSize || 0);
    const playable = kind === "video" || kind === "voice";
    const drawing = kind === "doc" && isDrawingFile(name);

    const meta = [
        getItemTypeLabel(item, kind),
        size > 0 ? formatBytes(size) : "",
        playable ? formatDuration(item?.mediaDurationSec) : "",
        getSenderName(item),
        formatDate(item?.createdAt),
    ].filter(Boolean).join(" · ");

    let openLabel = "";
    if (kind === "image") openLabel = "Открыть";
    if (drawing) openLabel = "Смотреть";
    if (kind === "video") openLabel = expanded ? "Свернуть" : "Смотреть";
    if (kind === "voice") openLabel = expanded ? "Свернуть" : "Слушать";

    return (
        <div className={`media-file-AO ${selected ? "is-selected" : ""}`}>
            <PickBox checked={selected} onChange={() => onToggle(id)} name={name} />

            <button
                type="button"
                className="media-file-AO__thumb"
                onClick={() => onOpen(item)}
                title={openLabel || "Скачать"}
            >
                <FileThumb item={item} kind={kind} name={name} />
            </button>

            <div className="media-file-AO__main">
                <b title={name}>{name}</b>
                <small>{meta}</small>
            </div>

            <div className="media-file-AO__actions">
                {!!openLabel && (
                    <button type="button" onClick={() => onOpen(item)}>{openLabel}</button>
                )}
                <button type="button" onClick={() => onDownload(item)} disabled={downloading}>
                    {downloading ? "Скачиваем…" : "Скачать"}
                </button>
                <button type="button" onClick={() => onShare(item)}>Поделиться</button>
            </div>

            {expanded && playable && (
                <div className="media-file-AO__preview">
                    <SecureMessageAttachment message={item} />
                </div>
            )}
        </div>
    );
});

export default function MediaModal({ chatId, type = "image", onClose }) {
    const dispatch = useDispatch();
    const store = useStore();
    const userId = useSelector((state) => state.auth?.user?._id || state.auth?.user?.id || null);
    const sendingProgress = useSelector((state) => Number(state.message?.sendingProgress) || 0);

    const scrollRef = useRef(null);
    const fileInputRef = useRef(null);
    const mountedRef = useRef(true);
    const cancelRef = useRef(false); // окно закрыли: оставшиеся файлы не трогаем
    const workRef = useRef(false); // идёт загрузка или удаление
    const archiveBusyRef = useRef(false);
    const linkTimerRef = useRef(null);
    const dragDepthRef = useRef(0);

    // Постраничная загрузка списка. Всё в ref, чтобы не было устаревших значений.
    const seqRef = useRef(0); // номер актуального запроса списка
    const busyRef = useRef(false);
    const pagingRef = useRef({ cursor: null, offset: null, hasMore: false, failed: false });
    const summarySeqRef = useRef(0);

    const [tab, setTab] = useState(() => normalizeTab(type));
    const [query, setQuery] = useState("");
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState("date");
    // Фильтр во вкладке «Документы»: all | office | model3d | cad | cnc | design | other.
    const [docGroup, setDocGroup] = useState("all");

    const [items, setItems] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [reloadNonce, setReloadNonce] = useState(0);

    const [previewId, setPreviewId] = useState("");
    const [expandedId, setExpandedId] = useState("");
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [downloadingId, setDownloadingId] = useState("");

    const [summary, setSummary] = useState(null);
    const [summaryError, setSummaryError] = useState("");

    const [archive, setArchive] = useState(IDLE_STATUS);
    const [removal, setRemoval] = useState(IDLE_STATUS);
    const [upload, setUpload] = useState(IDLE_UPLOAD);
    const [actionError, setActionError] = useState("");
    const [dragActive, setDragActive] = useState(false);

    const isFileTab = tab !== "link";

    useEffect(() => {
        mountedRef.current = true;
        cancelRef.current = false;

        return () => {
            mountedRef.current = false;
            cancelRef.current = true;
            if (linkTimerRef.current) window.clearTimeout(linkTimerRef.current);
        };
    }, []);

    // Если окно открыли заново с другим типом — переключаем вкладку.
    useEffect(() => {
        setTab(normalizeTab(type));
    }, [type]);

    // Поиск уходит на сервер не на каждую букву, а после короткой паузы.
    useEffect(() => {
        const timer = window.setTimeout(() => setSearch(query.trim()), SEARCH_DELAY_MS);

        return () => window.clearTimeout(timer);
    }, [query]);

    const loadSummary = useCallback(async () => {
        if (!chatId) return;

        summarySeqRef.current += 1;
        const seq = summarySeqRef.current;

        try {
            const res = await axios.get(`/chat/${encodeURIComponent(chatId)}/media/summary`);

            if (!mountedRef.current || seq !== summarySeqRef.current) return;

            setSummary(res?.data || null);
            setSummaryError("");
        } catch (requestError) {
            if (!mountedRef.current || seq !== summarySeqRef.current) return;

            setSummaryError(getErrorText(requestError, "Не удалось получить сводку по файлам."));
        }
    }, [chatId]);

    useEffect(() => {
        setSummary(null);
        setSummaryError("");
        loadSummary();
    }, [loadSummary]);

    // reset = true: загрузить список с начала (новая вкладка, поиск, сортировка).
    // auto = true: вызов от прокрутки, после ошибки сам не повторяется.
    const loadPage = useCallback(async (reset = false, auto = false) => {
        if (!chatId) {
            if (reset) {
                seqRef.current += 1;
                busyRef.current = false;
                pagingRef.current = { cursor: null, offset: null, hasMore: false, failed: false };
                setHasMore(false);
                setLoading(false);
            }
            return;
        }

        if (!reset) {
            if (busyRef.current || !pagingRef.current.hasMore) return;
            if (auto && pagingRef.current.failed) return;
        }

        if (reset) {
            seqRef.current += 1;
            pagingRef.current = { cursor: null, offset: null, hasMore: false, failed: false };
            setHasMore(false);
        }

        const seq = seqRef.current;
        const fileTab = tab !== "link";
        const bySize = fileTab && sort === "size";
        const params = { type: tab, limit: PAGE_LIMIT };

        if (fileTab) {
            params.sort = sort;
            if (search) params.q = search;
        }

        if (tab === "doc" && docGroup !== "all") params.docKind = docGroup;

        if (!reset) {
            if (bySize) params.offset = pagingRef.current.offset;
            else params.before = pagingRef.current.cursor;
        }

        busyRef.current = true;
        setLoading(true);
        setError("");

        try {
            const res = await axios.get(`/chat/${encodeURIComponent(chatId)}/media`, { params });

            // Пока ждали ответ, пользователь мог сменить вкладку или поиск.
            if (!mountedRef.current || seq !== seqRef.current) return;

            const data = res?.data || {};
            const pageItems = Array.isArray(data.items) ? data.items : [];

            // По дате сервер отдаёт страницу от старых к новым, а на экране новые сверху.
            const ordered = bySize ? pageItems : pageItems.slice().reverse();
            let more = false;

            if (bySize) {
                more = data.nextOffset != null && pageItems.length > 0;
                pagingRef.current = {
                    cursor: null,
                    offset: more ? Number(data.nextOffset) : null,
                    hasMore: more,
                    failed: false,
                };
            } else {
                more = !!data.nextCursor;
                pagingRef.current = {
                    cursor: data.nextCursor || null,
                    offset: null,
                    hasMore: more,
                    failed: false,
                };
            }

            setItems((prev) => (reset ? ordered : mergeItems(prev, ordered)));
            setHasMore(more);
        } catch (requestError) {
            if (!mountedRef.current || seq !== seqRef.current) return;

            setError(getErrorText(requestError, "Не удалось загрузить вложения."));

            if (reset) {
                setItems([]);
                setHasMore(false);
            } else {
                // Кнопка «Показать ещё» остаётся, но прокрутка сама больше не дёргает сервер.
                pagingRef.current.failed = true;
            }
        } finally {
            if (seq === seqRef.current) {
                busyRef.current = false;
                if (mountedRef.current) setLoading(false);
            }
        }
    }, [chatId, tab, sort, search, docGroup]);

    // Любая смена чата, вкладки, поиска или сортировки = новый список и пустой выбор.
    useEffect(() => {
        setItems([]);
        setSelectedIds(new Set());
        setPreviewId("");
        setExpandedId("");
        setError("");
        loadPage(true);
    }, [loadPage, reloadNonce]);

    useEffect(() => {
        const el = scrollRef.current;

        if (!el) return undefined;

        const handler = () => {
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 160) loadPage(false, true);
        };

        el.addEventListener("scroll", handler, { passive: true });

        return () => el.removeEventListener("scroll", handler);
    }, [loadPage]);

    const requestClose = useCallback(() => {
        if (workRef.current) {
            const sure = window.confirm(
                "Загрузка или удаление ещё не закончились. Закрыть окно? Оставшиеся файлы обработаны не будут."
            );

            if (!sure) return;
        }

        cancelRef.current = true;
        onClose?.();
    }, [onClose]);

    useEffect(() => {
        const handler = (event) => {
            if (event.key === "Escape" && !previewId) requestClose();
        };

        window.addEventListener("keydown", handler);

        return () => window.removeEventListener("keydown", handler);
    }, [previewId, requestClose]);

    const attachmentItems = useMemo(
        () => items.filter(hasSecureChatAttachment),
        [items]
    );

    // В просмотре листаются фото и чертежи (DXF/SVG).
    const imageItems = useMemo(
        () => attachmentItems.filter((item) => getChatAttachmentType(item) === "image" || isDrawingItem(item)),
        [attachmentItems]
    );

    const selectedItems = useMemo(
        () => attachmentItems.filter((item) => selectedIds.has(getId(item))),
        [attachmentItems, selectedIds]
    );

    const selectedBytes = useMemo(
        () => selectedItems.reduce((sum, item) => sum + (Number(item?.fileSize) || 0), 0),
        [selectedItems]
    );

    const selectedCount = selectedItems.length;
    const allSelected = attachmentItems.length > 0 && selectedCount === attachmentItems.length;
    const working = upload.busy || removal.busy;

    const previewIndex = Math.max(
        0,
        imageItems.findIndex((item) => String(item?._id || "") === String(previewId))
    );

    const toggleSelected = useCallback((id) => {
        if (!id) return;

        setSelectedIds((prev) => {
            const next = new Set(prev);

            if (next.has(id)) next.delete(id);
            else next.add(id);

            return next;
        });
    }, []);

    const toggleAll = () => {
        setSelectedIds(allSelected ? new Set() : new Set(attachmentItems.map(getId)));
    };

    const handleDownload = useCallback(async (message) => {
        const id = getId(message);

        if (!id) return;

        setDownloadingId(id);
        setActionError("");

        try {
            const asset = await fetchChatAttachment({ message, download: true });

            downloadBlob({
                blob: asset.blob,
                filename: asset.filename || getChatAttachmentName(message),
            });
        } catch (_error) {
            if (mountedRef.current) {
                setActionError(
                    `Не удалось скачать «${getChatAttachmentName(message)}». ` +
                    "Большие файлы надёжнее скачивать так: отметьте файл и нажмите «Скачать архивом»."
                );
            }
        } finally {
            if (mountedRef.current) setDownloadingId((current) => (current === id ? "" : current));
        }
    }, []);

    const handleShare = useCallback(async (message) => {
        setActionError("");

        try {
            const asset = await fetchChatAttachment({ message });

            await shareBlobFile({
                blob: asset.blob,
                filename: asset.filename || getChatAttachmentName(message),
                mimeType: asset.mimeType || getChatAttachmentMimeType(message),
                title: labels[tab] || "Медиа чата",
            });
        } catch (_error) {
            if (mountedRef.current) {
                setActionError(`Не удалось поделиться файлом «${getChatAttachmentName(message)}».`);
            }
        }
    }, [tab]);

    const openItem = useCallback((item) => {
        const id = getId(item);
        const kind = getChatAttachmentType(item);

        if (kind === "image" || isDrawingItem(item)) {
            setPreviewId(id);
            return;
        }

        if (kind === "video" || kind === "voice") {
            setExpandedId((current) => (current === id ? "" : id));
            return;
        }

        handleDownload(item);
    }, [handleDownload]);

    // messageIds = null: архив всей текущей вкладки.
    const requestArchive = useCallback(async (messageIds = null) => {
        if (!chatId || archiveBusyRef.current) return;

        archiveBusyRef.current = true;

        if (linkTimerRef.current) {
            window.clearTimeout(linkTimerRef.current);
            linkTimerRef.current = null;
        }

        setArchive({ busy: true, text: "Готовим архив…", error: "", link: "" });

        try {
            const body = messageIds
                ? { messageIds }
                : { type: tab, ...(tab === "doc" && docGroup !== "all" ? { docKind: docGroup } : {}) };
            const res = await axios.post(`/chat/${encodeURIComponent(chatId)}/media/archive`, body);
            const data = res?.data || {};

            if (!data.path) throw new Error("archive path is empty");

            const url = buildApiUrl(data.path);
            const count = Number(data.count) || 0;

            startBrowserDownload(url, data.fileName);

            if (!mountedRef.current) return;

            setArchive({
                busy: false,
                text: `Архив готов: ${count} ${pluralFiles(count)}, ${formatBytes(data.bytes)}. Скачивание началось.`,
                error: "",
                link: url,
            });

            // Ссылка на архив живёт ограниченное время — потом прячем её.
            const lifetimeMs = Math.min((Number(data.expiresInSec) || 0) * 1000, MAX_TIMER_MS);

            if (lifetimeMs > 0) {
                linkTimerRef.current = window.setTimeout(() => {
                    setArchive((current) => (current.link === url ? { ...current, link: "" } : current));
                }, lifetimeMs);
            }
        } catch (archiveError) {
            if (mountedRef.current) {
                setArchive({ busy: false, text: "", error: getArchiveErrorText(archiveError), link: "" });
            }
        } finally {
            archiveBusyRef.current = false;
        }
    }, [chatId, tab, docGroup]);

    const handleDeleteSelected = useCallback(async () => {
        const targets = selectedItems;

        if (!targets.length || workRef.current) return;

        const sure = window.confirm(
            `Удалить выбранные файлы: ${targets.length}? Они пропадут из чата у всех участников. Вернуть их будет нельзя.`
        );

        if (!sure) return;

        workRef.current = true;

        const listSeq = seqRef.current;
        const removedIds = [];
        const reasons = [];

        for (let index = 0; index < targets.length; index += 1) {
            if (cancelRef.current) break;

            const id = getId(targets[index]);

            if (mountedRef.current) {
                setRemoval({
                    busy: true,
                    text: `Удаляем ${index + 1} из ${targets.length}…`,
                    error: "",
                    link: "",
                });
            }

            try {
                await axios.delete(`/messages/${encodeURIComponent(id)}`);
                removedIds.push(id);
                dispatch(deleteMessageSocket(id));
            } catch (deleteError) {
                if (deleteError?.response?.status === 404) {
                    // Файл уже удалён кем-то другим — просто убираем его из списка.
                    removedIds.push(id);
                    dispatch(deleteMessageSocket(id));
                } else {
                    reasons.push(getErrorText(deleteError, "Не удалось удалить файл."));
                }
            }
        }

        workRef.current = false;

        if (!mountedRef.current) return;

        if (removedIds.length) {
            const removedSet = new Set(removedIds);

            setItems((prev) => prev.filter((item) => !removedSet.has(getId(item))));
            setSelectedIds((prev) => {
                const next = new Set(prev);
                removedIds.forEach((id) => next.delete(id));
                return next;
            });
            setExpandedId((current) => (removedSet.has(current) ? "" : current));

            // При сортировке по размеру страницы считаются по номеру строки:
            // после удаления список на сервере стал короче.
            if (listSeq === seqRef.current && pagingRef.current.offset != null) {
                pagingRef.current.offset = Math.max(0, pagingRef.current.offset - removedIds.length);
            }

            loadSummary();
        }

        setRemoval({
            busy: false,
            text: removedIds.length ? `Удалено: ${removedIds.length} ${pluralFiles(removedIds.length)}.` : "",
            error: reasons.length
                ? `Не удалось удалить: ${reasons.length} ${pluralFiles(reasons.length)}. Причина: ${groupReasons(reasons)}`
                : "",
            link: "",
        });
    }, [selectedItems, dispatch, loadSummary]);

    const uploadFiles = useCallback(async (fileList, skippedFolders = 0) => {
        const files = Array.from(fileList || []).filter(Boolean);

        if (!files.length && !skippedFolders) return;

        if (!chatId) {
            setUpload({ ...IDLE_UPLOAD, lines: [{ kind: "error", text: "Чат не выбран. Загрузить файлы некуда." }] });
            return;
        }

        if (workRef.current) {
            setActionError("Дождитесь окончания текущей операции, потом добавьте новые файлы.");
            return;
        }

        const tooBig = files.filter((file) => file.size > MAX_UPLOAD_BYTES);
        const empty = files.filter((file) => !file.size);
        const queue = files.filter((file) => file.size > 0 && file.size <= MAX_UPLOAD_BYTES);
        const lines = [];

        if (tooBig.length) {
            lines.push({
                kind: "error",
                text: `Пропущено, файл больше 1 ГБ: ${joinNames(tooBig.map((file) => file.name))}.`,
            });
        }

        if (empty.length) {
            lines.push({
                kind: "error",
                text: `Пропущено, файл пустой: ${joinNames(empty.map((file) => file.name))}.`,
            });
        }

        if (skippedFolders) {
            lines.push({
                kind: "error",
                text: "Папки целиком загрузить нельзя. Откройте папку и перетащите сами файлы.",
            });
        }

        if (!queue.length) {
            setUpload({ ...IDLE_UPLOAD, lines });
            return;
        }

        workRef.current = true;
        setActionError("");

        let done = 0;
        const failed = [];

        for (let index = 0; index < queue.length; index += 1) {
            if (cancelRef.current) break;

            const file = queue[index];

            if (mountedRef.current) {
                setUpload({
                    busy: true,
                    index: index + 1,
                    total: queue.length,
                    name: file.name,
                    lines,
                });
            }

            let sent = null;
            let reason = "";

            try {
                // Тот же action, которым чат отправляет файл: один файл — один запрос.
                sent = await dispatch(sendFileMessage(chatId, { sender: userId, text: "" }, file));

                if (!sent) {
                    // Текст ошибки action кладёт в redux, а не возвращает.
                    const sendError = store.getState()?.message?.sendError;
                    reason = typeof sendError === "string" ? sendError : "";
                }
            } catch (uploadError) {
                reason = getErrorText(uploadError, "");
            }

            if (sent) done += 1;
            else failed.push(`${file.name} — ${translateSendError(reason)}`);
        }

        workRef.current = false;

        if (!mountedRef.current) return;

        const resultLines = [...lines];

        if (done) {
            resultLines.unshift({
                kind: "ok",
                text: `Загружено: ${done} из ${queue.length}.`,
            });
        }

        if (failed.length) {
            resultLines.push({
                kind: "error",
                text: `Не загружено: ${failed.length} ${pluralFiles(failed.length)}. ${joinNames(failed, 3)}.`,
            });
        }

        setUpload({ ...IDLE_UPLOAD, lines: resultLines });

        if (done) {
            setReloadNonce((value) => value + 1);
            loadSummary();
        }
    }, [chatId, userId, dispatch, store, loadSummary]);

    const handleFileInput = (event) => {
        const files = Array.from(event.target.files || []);

        event.target.value = "";
        uploadFiles(files);
    };

    const handleDragEnter = (event) => {
        // Пока открыт просмотр фото, перетаскивание не принимаем.
        if (!hasDraggedFiles(event) || previewId) return;

        event.preventDefault();
        dragDepthRef.current += 1;
        setDragActive(true);
    };

    const handleDragOver = (event) => {
        if (!hasDraggedFiles(event)) return;

        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
    };

    const handleDragLeave = (event) => {
        if (!hasDraggedFiles(event)) return;

        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (!dragDepthRef.current) setDragActive(false);
    };

    const handleDrop = (event) => {
        if (!hasDraggedFiles(event)) return;

        event.preventDefault();
        dragDepthRef.current = 0;
        setDragActive(false);

        if (previewId) return;

        const dropped = pickDroppedFiles(event.dataTransfer);
        uploadFiles(dropped.files, dropped.folders);
    };

    // Если файл уронили мимо окна, браузер не должен открывать его вместо админки.
    const blockBrowserDrop = (event) => {
        if (hasDraggedFiles(event)) event.preventDefault();
    };

    const handleBackdropDrop = (event) => {
        blockBrowserDrop(event);
        dragDepthRef.current = 0;
        setDragActive(false);
    };

    const changeTab = (nextTab) => {
        if (nextTab === tab) return;

        // Старый список и выбор убираем сразу, чтобы они не мелькнули на новой вкладке.
        setItems([]);
        setSelectedIds(new Set());
        setTab(nextTab);
        setDocGroup("all");
        setActionError("");
    };

    const changeDocGroup = (nextGroup) => {
        if (nextGroup === docGroup) return;
        setItems([]);
        setSelectedIds(new Set());
        setDocGroup(nextGroup);
        setActionError("");
    };

    const changeSort = (nextSort) => {
        if (nextSort !== sort) setSort(nextSort);
    };

    // Сводка по чату.
    const totalBytes = Number(summary?.total?.bytes) || 0;
    const totalCount = Number(summary?.total?.count) || 0;
    const disk = summary?.disk || null;
    const diskTotal = Number(disk?.totalBytes) || 0;
    const diskFree = Number(disk?.freeBytes) || 0;
    const diskUsed = disk && disk.usedBytes != null
        ? Number(disk.usedBytes) || 0
        : Math.max(0, diskTotal - diskFree);
    const diskPercent = diskTotal > 0 ? Math.min(100, Math.max(0, (diskUsed / diskTotal) * 100)) : 0;
    const diskTone = diskPercent >= 90 ? "is-danger" : (diskPercent >= 75 ? "is-warn" : "");

    const groupStat = tab === "doc" && docGroup !== "all" ? summary?.byDocKind?.[docGroup] : null;
    const tabStat = summary ? (groupStat || (tab === "all" ? summary.total : summary.byType?.[tab])) : null;
    const tabCount = tabStat ? Number(tabStat.count) || 0 : null;
    const canArchiveAll = isFileTab && (
        attachmentItems.length > 0 ||
        (tabCount != null ? tabCount > 0 : !!search)
    );

    const uploadPercent = upload.busy && upload.total > 0
        ? Math.min(100, Math.round(((upload.index - 1) + Math.min(100, sendingProgress) / 100) / upload.total * 100))
        : 0;

    const visibleCount = isFileTab ? attachmentItems.length : items.length;
    const emptyText = isFileTab
        ? (search
            ? "По этому названию ничего не найдено"
            : tab === "doc" && docGroup !== "all"
                ? "Таких документов в этом чате нет"
                : "Файлов пока нет. Перетащите их сюда или нажмите «Загрузить файлы».")
        : "Ничего нет";

    const hasStatus = upload.busy || upload.lines.length > 0 ||
        archive.busy || !!archive.text || !!archive.error ||
        removal.busy || !!removal.text || !!removal.error ||
        !!actionError;

    return (
        <div
            className="chat-modal-AO"
            onMouseDown={(event) => {
                // Закрываем только по клику на затемнённый фон. Просмотрщик фото
                // рисуется через портал, и его клики тоже всплывают сюда.
                if (event.target === event.currentTarget) requestClose();
            }}
            onDragOver={blockBrowserDrop}
            onDrop={handleBackdropDrop}
        >
            <div
                className="chat-modal-AO__body chat-modal-AO__body--media"
                onMouseDown={(event) => event.stopPropagation()}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <div className="chat-modal-AO__head">
                    <div className="media-head-AO">
                        <div className="chat-modal-AO__title">Вложения чата</div>

                        <div className="media-tabs-AO" role="tablist">
                            {TABS.map((item) => (
                                <button
                                    key={item.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={tab === item.key}
                                    className={tab === item.key ? "is-active" : ""}
                                    onClick={() => changeTab(item.key)}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <button type="button" onClick={requestClose} title="Закрыть">✕</button>
                </div>

                <div className="media-panel-AO">
                    <div className="media-summary-AO">
                        {summary ? (
                            <>
                                <div className="media-summary-AO__total">
                                    <span>Файлы этого чата</span>
                                    <strong>
                                        {formatBytes(totalBytes)}
                                        <small> · {totalCount} {pluralFiles(totalCount)}</small>
                                    </strong>
                                </div>

                                <div className="media-summary-AO__chips">
                                    {SUMMARY_TYPES.map((item) => {
                                        const stat = summary.byType?.[item.key] || {};
                                        const count = Number(stat.count) || 0;

                                        return (
                                            <span key={item.key} title={`${count} ${pluralFiles(count)}`}>
                                                {item.label}
                                                <b>{formatBytes(stat.bytes)}</b>
                                            </span>
                                        );
                                    })}
                                </div>

                                {!!disk && diskTotal > 0 && (
                                    <div className="media-summary-AO__disk">
                                        <small>
                                            Свободно на сервере: {formatBytes(diskFree)} из {formatBytes(diskTotal)}
                                        </small>
                                        <div
                                            className={`media-bar-AO ${diskTone}`}
                                            title={`Занято ${Math.round(diskPercent)}%`}
                                        >
                                            <span style={{ width: `${diskPercent}%` }} />
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="media-summary-AO__note">
                                {summaryError || "Считаем размер файлов…"}
                            </div>
                        )}
                    </div>

                    <div className="media-toolbar-AO">
                        {isFileTab && (
                            <input
                                type="search"
                                className="media-toolbar-AO__search"
                                placeholder="Поиск по названию файла"
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                            />
                        )}

                        {isFileTab && (
                            <div className="media-toolbar-AO__sort">
                                <button
                                    type="button"
                                    className={sort === "date" ? "is-active" : ""}
                                    onClick={() => changeSort("date")}
                                >
                                    По дате
                                </button>
                                <button
                                    type="button"
                                    className={sort === "size" ? "is-active" : ""}
                                    onClick={() => changeSort("size")}
                                >
                                    По размеру
                                </button>
                            </div>
                        )}

                        <div className="media-toolbar-AO__actions">
                            <button
                                type="button"
                                className="media-btn-AO is-primary"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={working || !chatId}
                                title="До 1 ГБ на один файл. Можно перетащить файлы прямо в окно."
                            >
                                Загрузить файлы
                            </button>

                            {isFileTab && (
                                <button
                                    type="button"
                                    className="media-btn-AO"
                                    onClick={() => requestArchive(null)}
                                    disabled={archive.busy || !canArchiveAll}
                                    title={
                                        tabStat
                                            ? `Все файлы вкладки одним архивом: ${formatBytes(tabStat.bytes)}. Поиск не учитывается.`
                                            : "Все файлы вкладки одним архивом. Поиск не учитывается."
                                    }
                                >
                                    Скачать всё архивом
                                </button>
                            )}
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            hidden
                            onChange={handleFileInput}
                        />
                    </div>

                    {tab === "doc" && (
                        <div className="media-doc-groups-AO" role="tablist" aria-label="Какие документы показать">
                            {DOC_GROUPS.map((group) => {
                                const stat = group.key === "all" ? summary?.byType?.doc : summary?.byDocKind?.[group.key];
                                const count = stat ? Number(stat.count) || 0 : null;

                                return (
                                    <button
                                        key={group.key}
                                        type="button"
                                        role="tab"
                                        aria-selected={docGroup === group.key}
                                        className={docGroup === group.key ? "is-active" : ""}
                                        onClick={() => changeDocGroup(group.key)}
                                        disabled={group.key !== "all" && count === 0 && docGroup !== group.key}
                                    >
                                        {group.label}
                                        {count != null ? <small>{count}</small> : null}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {isFileTab && attachmentItems.length > 0 && (
                        <div className="media-select-AO">
                            <label className="media-check-AO">
                                <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                                <span>Выбрать все</span>
                            </label>

                            <span className="media-select-AO__count">
                                Выбрано {selectedCount} · {formatBytes(selectedBytes)}
                            </span>

                            <div className="media-select-AO__actions">
                                <button
                                    type="button"
                                    className="media-btn-AO"
                                    onClick={() => requestArchive(selectedItems.map(getId))}
                                    disabled={!selectedCount || archive.busy}
                                >
                                    Скачать архивом
                                </button>
                                <button
                                    type="button"
                                    className="media-btn-AO is-danger"
                                    onClick={handleDeleteSelected}
                                    disabled={!selectedCount || working}
                                >
                                    Удалить
                                </button>
                            </div>
                        </div>
                    )}

                    {hasStatus && (
                        <div className="media-status-AO">
                            {upload.busy && (
                                <div className="media-status-AO__line" role="status">
                                    <span>
                                        Загружаем {upload.index} из {upload.total}: {upload.name}
                                        {sendingProgress > 0 && sendingProgress < 100 ? ` · ${sendingProgress}%` : ""}
                                    </span>
                                    <div className="media-bar-AO">
                                        <span style={{ width: `${uploadPercent}%` }} />
                                    </div>
                                </div>
                            )}

                            {upload.lines.map((line, index) => (
                                <div
                                    key={`upload-${index}`}
                                    className={`media-status-AO__line is-${line.kind}`}
                                    role={line.kind === "error" ? "alert" : "status"}
                                >
                                    <span>{line.text}</span>
                                    {!upload.busy && index === 0 && (
                                        <button type="button" onClick={() => setUpload(IDLE_UPLOAD)} title="Скрыть">✕</button>
                                    )}
                                </div>
                            ))}

                            {(archive.busy || !!archive.text) && (
                                <div className={`media-status-AO__line ${archive.busy ? "" : "is-ok"}`} role="status">
                                    <span>
                                        {archive.text}
                                        {!!archive.link && (
                                            <>
                                                {" "}
                                                <a href={archive.link} download>Не началось? Скачать по ссылке</a>
                                            </>
                                        )}
                                    </span>
                                    {!archive.busy && (
                                        <button type="button" onClick={() => setArchive(IDLE_STATUS)} title="Скрыть">✕</button>
                                    )}
                                </div>
                            )}

                            {!!archive.error && (
                                <div className="media-status-AO__line is-error" role="alert">
                                    <span>{archive.error}</span>
                                    <button type="button" onClick={() => setArchive(IDLE_STATUS)} title="Скрыть">✕</button>
                                </div>
                            )}

                            {(removal.busy || !!removal.text) && (
                                <div className={`media-status-AO__line ${removal.busy ? "" : "is-ok"}`} role="status">
                                    <span>{removal.text}</span>
                                    {!removal.busy && !removal.error && (
                                        <button type="button" onClick={() => setRemoval(IDLE_STATUS)} title="Скрыть">✕</button>
                                    )}
                                </div>
                            )}

                            {!!removal.error && (
                                <div className="media-status-AO__line is-error" role="alert">
                                    <span>{removal.error}</span>
                                    <button type="button" onClick={() => setRemoval(IDLE_STATUS)} title="Скрыть">✕</button>
                                </div>
                            )}

                            {!!actionError && (
                                <div className="media-status-AO__line is-error" role="alert">
                                    <span>{actionError}</span>
                                    <button type="button" onClick={() => setActionError("")} title="Скрыть">✕</button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div ref={scrollRef} className="chat-modal-AO__content">
                    {tab === "all" && attachmentItems.length > 0 && (
                        <div className="media-files-AO">
                            {attachmentItems.map((item) => {
                                const id = getId(item);

                                return (
                                    <FileRow
                                        key={id}
                                        item={item}
                                        selected={selectedIds.has(id)}
                                        expanded={expandedId === id}
                                        downloading={downloadingId === id}
                                        onToggle={toggleSelected}
                                        onOpen={openItem}
                                        onDownload={handleDownload}
                                        onShare={handleShare}
                                    />
                                );
                            })}
                        </div>
                    )}

                    {tab === "image" && (
                        <div className="media-grid-AO media-grid-AO--image">
                            {attachmentItems.map((item) => {
                                const id = getId(item);
                                const picked = selectedIds.has(id);

                                return (
                                    <div
                                        key={item._id}
                                        className={`media-card-AO media-card-AO--image ${picked ? "is-selected" : ""}`}
                                    >
                                        <PickBox
                                            checked={picked}
                                            onChange={() => toggleSelected(id)}
                                            name={getChatAttachmentName(item, "Фотография")}
                                        />
                                        <SecureMessageAttachment
                                            message={item}
                                            onOpen={() => setPreviewId(String(item._id))}
                                        />
                                        <span>{getSenderName(item)}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {tab === "video" && (
                        <div className="media-grid-AO media-grid-AO--video">
                            {attachmentItems.map((item) => {
                                const id = getId(item);
                                const picked = selectedIds.has(id);

                                return (
                                    <div
                                        key={item._id}
                                        className={`media-card-AO media-card-AO--video ${picked ? "is-selected" : ""}`}
                                    >
                                        <PickBox
                                            checked={picked}
                                            onChange={() => toggleSelected(id)}
                                            name={getChatAttachmentName(item, "Видео")}
                                        />
                                        <SecureMessageAttachment message={item} />
                                        <div>{getSenderName(item)}</div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {tab === "voice" && (
                        <div className="media-list-AO">
                            {attachmentItems.map((item) => {
                                const id = getId(item);
                                const picked = selectedIds.has(id);

                                return (
                                    <div
                                        key={item._id}
                                        className={`media-row-AO media-row-AO--pick ${picked ? "is-selected" : ""}`}
                                    >
                                        <PickBox
                                            checked={picked}
                                            onChange={() => toggleSelected(id)}
                                            name={getChatAttachmentName(item, "Голосовое сообщение")}
                                        />
                                        <div className="media-row-AO__main">
                                            <div className="media-row-AO__head">
                                                <b>{getSenderName(item)}</b>
                                                <span>{formatDate(item.createdAt)}</span>
                                            </div>
                                            <SecureMessageAttachment message={item} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {tab === "doc" && (
                        <div className="media-list-AO">
                            {attachmentItems.map((item) => {
                                const id = getId(item);
                                const picked = selectedIds.has(id);

                                return (
                                    <div
                                        key={item._id}
                                        className={`media-row-AO media-row-AO--pick ${picked ? "is-selected" : ""}`}
                                    >
                                        <PickBox
                                            checked={picked}
                                            onChange={() => toggleSelected(id)}
                                            name={getChatAttachmentName(item, "Документ")}
                                        />
                                        <div className="media-row-AO__main">
                                            <SecureMessageAttachment
                                                message={item}
                                                onOpen={isDrawingItem(item) ? () => setPreviewId(id) : undefined}
                                            />
                                            <small>{getSenderName(item)} · {formatDate(item.createdAt)}</small>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {tab === "link" && (
                        <div className="media-list-AO">
                            {items.map((item) => {
                                const links = (item.text || "").match(/https?:\/\/\S+|www\.\S+/gi) || [];

                                if (!links.length) return null;

                                return (
                                    <div key={item._id} className="media-row-AO media-row-AO--link">
                                        <b>{getSenderName(item)}</b>
                                        {links.map((link, index) => {
                                            const href = link.startsWith("http") ? link : `https://${link}`;

                                            return <a key={`${item._id}-${index}`} href={href} target="_blank" rel="noreferrer">{link}</a>;
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {error && (
                        <div className="chat-search-AO__error" role="alert">
                            {error}
                            {!visibleCount && !loading && (
                                <>
                                    {" "}
                                    <button
                                        type="button"
                                        className="media-link-btn-AO"
                                        onClick={() => setReloadNonce((value) => value + 1)}
                                    >
                                        Повторить
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                    {loading && <div className="chat-modal-AO__hint">Загрузка…</div>}
                    {!loading && hasMore && (
                        <button type="button" className="media-more-AO" onClick={() => loadPage(false)}>
                            Показать ещё
                        </button>
                    )}
                    {!loading && !visibleCount && !error && !hasMore && (
                        <div className="chat-modal-AO__hint">{emptyText}</div>
                    )}
                </div>

                {dragActive && (
                    <div className="media-drop-AO" aria-hidden="true">
                        <b>{working ? "Сначала дождитесь окончания текущей операции" : "Отпустите файлы — они загрузятся в этот чат"}</b>
                        <small>До 1 ГБ на один файл</small>
                    </div>
                )}
            </div>

            <SiteMediaViewer
                open={(tab === "image" || tab === "all" || tab === "doc") && !!previewId && imageItems.length > 0}
                items={imageItems}
                activeIndex={previewIndex}
                onActiveIndexChange={(nextIndex, nextItem) => setPreviewId(String(nextItem?._id || ""))}
                onClose={() => setPreviewId("")}
                resolveItem={(item) => fetchChatAttachmentForView({ message: item })}
                getItemMimeType={(item) => getChatAttachmentMimeType(item) || "image/jpeg"}
                getTitle={(item) => getChatAttachmentName(item, "Фотография")}
                getSubtitle={(item) => `${getSenderName(item)} · ${formatDate(item?.createdAt)}`}
                getAlt={(item) => getChatAttachmentName(item, "Фотография")}
                onDownload={handleDownload}
                onShare={handleShare}
                shareLabel="Поделиться"
                loadingText="Загружаем защищённое фото…"
                emptyText="Фото недоступно"
            />
        </div>
    );
}
